import { useState } from 'react';
import type { Bulle, HabitEnBase, Maillon } from '../partage/api/types';
import { ContactDeLaFiche } from './ContactDeLaFiche';
import { CoqueDeLaFiche, GestesDuPied, NotesDeLaFiche, TeteDeLaFiche } from './CoqueDeLaFiche';
import { Dependances, PoserUneCondition } from './Dependances';
import { ConditionsDeLaFace, ContactDeLaFace, EtatsDeLaFace, PastillesDeLaFace, QuandDeLaFace } from './FaceDeLaFiche';
import { FicheAuPouce } from './FicheAuPouce';
import { HautDeLaFiche, type TypeEtPapierDeLaFiche } from './HautDeLaFiche';
import { LiensDeLaFiche } from './LiensDeLaFiche';
import { laMere, remonteDansMaintenant } from './ficheEnMots';
import type { AnnulationDeLaFiche } from './GestesDeLaFiche';
import { NoteDeLaFiche } from './NoteDeLaFiche';
import { PanneauDesReglages, type Intercalaire } from './PanneauDesReglages';
import { PlanDeLaFiche } from './PlanDeLaFiche';
import { PlansDeVieDeLaFiche, type Ajout } from './PiecesDeLaFiche';
import type { HabitDuProjetPourUneBulle } from './HabitDeLaBulle';
import { useAuPouce, useCouche } from '../partage/surface';
import { useEchap } from '../partage/useEchap';
import { useMecaniqueDeLaFiche } from './useMecaniqueDeLaFiche';
import { CREUSER, DEPLACER, motsDeSituation, RENOMMER_LA_BULLE, SUPPRIMER_LA_BULLE } from '../partage/vocabulaire';
import './Fiche.css';

/** Ce que l'écran qui ouvre une fiche lui donne : les mêmes données et les mêmes gestes partout. */
export interface ProprietesDeLaFiche {
  /** Lue sur une feuille, elle porte aussi sa forme, son trait et la couleur héritée (habit-des-bulles). */
  bulle: Bulle & Partial<HabitEnBase>;
  chemin: Maillon[];
  couleur: string | null;
  onChanger: (champs: Partial<Bulle>) => void;
  onValider: () => void;
  /**
   * « J'attends un retour ». Au téléphone, il part avec le délai qu'on a choisi dans la petite
   * feuille (3 jours, 10 jours, 3 semaines, une date précise) : un seul geste, une seule écriture.
   */
  onAttendre: (champs?: Partial<Bulle>) => void;
  /** « Annuler » après « C'est fait » ou « J'attends un retour » : se raviser (ticket 29). */
  onDefaire: () => void;
  /**
   * Ce que l'annulation rendra. `etatAvant` est `null` quand le geste n'a pas été retenu
   * (page rechargée, état posé ailleurs) : le clic remet alors à faire, et le bouton le dit.
   */
  annulation: AnnulationDeLaFiche;
  onCreuser: () => void;
  onFermer: () => void;
  /** La bulle vient d'être supprimée, avec ce qu'elle contenait : la fiche n'a plus d'objet. */
  onSupprimee: () => void;
  /** La bulle vient d'être rangée ailleurs, avec ce qu'elle contient : la fiche se ferme. */
  onDeplacee: () => void;
  /**
   * Ouvrir la fiche d'une sœur, par « ‹ › ». Tant que l'écran appelant ne le fournit pas, les
   * flèches se dessinent mais ne se cliquent pas : elles ne doivent pas faire semblant.
   */
  onSoeur?: (id: string) => void;
  /** Ouvrir la fiche de la mère, par « ‹ la mère ». Sans lui, le bouton ne se clique pas. */
  onMere?: () => void;
  /**
   * Ouvrir la fiche d'une de ses étapes, depuis « Ses étapes » (au téléphone, dans le panneau
   * Réglages). Sans lui, la liste ne se montre pas : une étape qui ne s'ouvre pas ferait
   * semblant de répondre.
   */
  onEtape?: (id: string) => void;
  /**
   * Ouvrir la fiche d'une bulle qu'elle attend, en touchant son nom dans la pastille « Attend »
   * (au téléphone, décision 16 de la revue). Sans lui, les noms se lisent sans se toucher.
   */
  onAutreBulle?: (id: string) => void;
  /**
   * Le type et le papier de la fiche, et ce qui les change (chantier types-de-fiche, modification
   * 02) : à l'ordinateur le menu « Type ▾ », au téléphone le ⚙ (chantier telephone-fiche, 01).
   */
  typeEtPapier?: TypeEtPapierDeLaFiche;
  /**
   * L'habit du projet, pour l'espace « Habit » de la fiche (chantier habit-des-bulles,
   * modification 04). À l'ordinateur seulement ; sans lui, l'espace ne se montre pas.
   */
  habitDuProjet?: HabitDuProjetPourUneBulle;
  /**
   * Au téléphone, dans un projet (chantier telephone-projet, 04) : la fiche se lit comme une
   * page, sous le fil, au lieu de monter du bas. Le même contenu.
   */
  enPage?: boolean;
}

/**
 * La fiche d'une bulle. Deux vues de la même fiche, sur les mêmes données et les mêmes gestes
 * (cadrage de `telephone-projets-fiche`, décision 11 : « la fiche tel est comme une autre vue de
 * la fiche ordi ») : sur l'ordinateur, le panneau validé le 2026-10-04 ; au téléphone, l'écran à
 * part du ticket 03 (`FicheAuPouce`), sa forme changeant vraiment. Les deux partagent leur
 * coque (`CoqueDeLaFiche`) et leur mécanique (`useMecaniqueDeLaFiche`).
 */
export function Fiche(props: ProprietesDeLaFiche) {
  const auPouce = useFicheAuPouce();
  return auPouce ? <FicheAuPouce {...props} /> : <FicheDeLOrdinateur {...props} />;
}

/**
 * La fiche du téléphone s'ouvre-t-elle ? Au pouce, ou le téléphone couché (chantier
 * telephone-fiche, 02 : couché, l'écran dépasse 720 px, mais c'est toujours un téléphone ;
 * une fenêtre d'ordinateur large et basse n'en est jamais un, `estCouche`). Le choix écrit une
 * fois, lu aussi pour son type (`FicheDeLaFeuille`).
 */
export function useFicheAuPouce(): boolean {
  const auPouce = useAuPouce();
  const couche = useCouche();
  return auPouce || couche;
}

/**
 * La fiche de l'ordinateur, un seul panneau (chantier `revue-design`, ticket 02 ; chantier
 * reglages-de-la-fiche, modifications 02 et 03 ; maquette du carnet `fiche-quatre-types.html`).
 * **La fiche montre, les Réglages changent** :
 *
 * - en haut, « Type : Classique ▾ », « ⚙ Réglages » et la croix (`HautDeLaFiche`) ; dessous,
 *   « ‹ la mère » pour remonter, « 3 sur 6 ‹ › » pour passer aux sœurs ; la fiche prend l'allure
 *   de son papier ;
 * - la face Classique, comme la maquette : le titre et son crayon ; les états ; les pastilles de ce
 *   qui est réglé ; « Remonte dans Maintenant : … » et la situation ; Quand ; le contact et le lieu
 *   en une ligne ; les plans de vie ; la note ; puis les gestes, et « Déplacer », « Supprimer la
 *   bulle » tout en bas (`FaceDeLaFiche`) ;
 * - les faces Note et Contact, la leur ; la face Plan, son schéma, puis ses « Conditions » (avec
 *   « + Condition ») et ses « Liens », dépliés : **seule la fiche Plan montre et pose les liens**
 *   (chantier liens-entre-bulles, ticket 05, décision 13 du cadrage) ;
 * - « ⚙ Réglages » retourne la fiche en panneau, à sa place, avec ses quatre intercalaires
 *   (`PanneauDesReglages`) ; un clic sur une pastille, sur Quand ou sur le contact l'ouvre sur
 *   l'intercalaire qui le règle.
 *
 * On y arrive par un DOUBLE-CLIC ; un simple clic descend dedans (SPEC §5.3).
 */
function FicheDeLOrdinateur({
  bulle,
  chemin,
  couleur,
  onChanger,
  onValider,
  onAttendre,
  onDefaire,
  annulation,
  onCreuser,
  onFermer,
  onSupprimee,
  onDeplacee,
  onSoeur,
  onMere,
  onAutreBulle,
  typeEtPapier,
  habitDuProjet,
}: ProprietesDeLaFiche) {
  /*
   * Ce qui s'ajoute sur la face Plan (une condition). Ça ne se mémorise nulle part : une autre
   * bulle s'ouvre sans.
   */
  const [ajout, setAjout] = useState<Ajout | null>(null);
  /*
   * Le panneau Réglages, à la place de la fiche, et son intercalaire ouvert (chantier
   * reglages-de-la-fiche, modification 02) ; `null` : la fiche. Une autre bulle s'ouvre sur sa fiche.
   */
  const [intercalaire, setIntercalaire] = useState<Intercalaire | null>(null);
  /*
   * Le titre en champ, la confirmation de suppression (ticket 10 de retours-testeurs) et le
   * choix de la destination (chantier `deplacer`), ouverts au-dessus de la fiche : la même
   * mécanique qu'au téléphone.
   */
  const mecanique = useMecaniqueDeLaFiche(bulle.id, () => {
    setAjout(null);
    setIntercalaire(null);
  });
  const { renommer } = mecanique;

  /* Ouvrir le panneau sur un intercalaire, ou y changer d'intercalaire. */
  const ouvrirLesReglages = (i: Intercalaire) => {
    setAjout(null);
    setIntercalaire(i);
  };
  /* « ‹ La fiche » : la fiche revient, à l'endroit. */
  const revenirALaFiche = () => {
    setAjout(null);
    setIntercalaire(null);
  };

  /*
   * Échap replie d'abord ce qui est ouvert sur la face (une condition à poser) ; puis, dans le
   * panneau Réglages, ramène à la fiche ; sinon il ferme la fiche. Le champ du titre le garde pour
   * lui ; la confirmation de suppression et le choix de la destination le prennent avant la fiche
   * (`ordreDEchap`).
   */
  useEchap(true, () => {
    if (ajout) setAjout(null);
    else if (intercalaire) revenirALaFiche();
    else onFermer();
  });

  const mere = laMere(chemin);
  const remonte = remonteDansMaintenant(bulle);
  const etat = bulle.etat;
  // « En réflexion » se dit par son état, allumé, et la situation ne le redit pas (décision 6).
  const situation = etat === 'sans_objet' ? null : motsDeSituation(bulle.situation);

  const type = typeEtPapier?.montree.type ?? 'classique';
  /* « ⚙ Réglages » retourne la fiche en panneau, sur État, et la remet à l'endroit (02). */
  const basculerLesReglages = () => (intercalaire ? revenirALaFiche() : ouvrirLesReglages('etat'));
  /* « + Condition » ouvre la recherche dessous ; un second clic la range. */
  const basculerLaCondition = () => setAjout((avant) => (avant === 'condition' ? null : 'condition'));

  /* Les conditions de la bulle Plan : le bloc des conditions, et la recherche d'une condition à poser. */
  const poserUneCondition = (
    <PoserUneCondition
      bulleId={bulle.id}
      titre={bulle.titre}
      delaiJours={bulle.delaiJours}
      ouvert={ajout === 'condition'}
      onFermer={() => setAjout(null)}
    />
  );
  const conditions = (
    <ConditionsDeLaFace onPoserUneCondition={basculerLaCondition}>
      <Dependances key={`conditions-${bulle.id}`} bulle={bulle} parent={mere} onAutreBulle={onAutreBulle} />
      {poserUneCondition}
    </ConditionsDeLaFace>
  );
  /* La note, en rubrique : sur la face Classique, et sous le schéma de la fiche Plan. */
  const note = (
    <div className="face-notes">
      <NotesDeLaFiche bulle={bulle} onChanger={onChanger} />
    </div>
  );

  /* La face Classique, comme la maquette : rien ne s'y règle. */
  const classique = (
    <>
      <EtatsDeLaFace etat={etat} />
      <PastillesDeLaFace bulle={bulle} onOuvrir={ouvrirLesReglages} />

      {remonte && (
        <p className="fiche-remonte">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path d="M12 19V5M6 11l6-6 6 6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{remonte}</span>
        </p>
      )}

      {situation && <p className={`fiche-situation fiche-situation--${bulle.situation.nom}`}>{situation}</p>}

      <QuandDeLaFace bulle={bulle} onOuvrir={ouvrirLesReglages} />
      <ContactDeLaFace bulle={bulle} onOuvrir={ouvrirLesReglages} />
      <div className="face-plans">
        <PlansDeVieDeLaFiche key={`plans-${bulle.id}`} />
      </div>
      {note}
    </>
  );

  return (
    <CoqueDeLaFiche
      bulle={bulle}
      chemin={chemin}
      couleur={couleur}
      mecanique={mecanique}
      onMere={onMere}
      onSoeur={onSoeur}
      onFermer={onFermer}
      onSupprimee={onSupprimee}
      onDeplacee={onDeplacee}
      papier={typeEtPapier?.montree.papier}
      sansNav={intercalaire !== null}
      type={typeEtPapier?.montree.type}
      haut={
        <HautDeLaFiche
          fiche={typeEtPapier ?? null}
          panneauOuvert={intercalaire !== null}
          onReglages={basculerLesReglages}
          onFermer={onFermer}
        />
      }
    >
      {/* La fiche Note est punaisée, comme une note posée (maquette `.punaise`). */}
      {type === 'note' && <span className="fiche-punaise" aria-hidden="true" />}

      {/* Le panneau Réglages prend la place de la fiche, sa ligne de la mère et son pied compris (02). */}
      {intercalaire ? (
        <div className="fiche-corps">
          <PanneauDesReglages
            bulle={bulle}
            chemin={chemin}
            intercalaire={intercalaire}
            onIntercalaire={ouvrirLesReglages}
            onRevenir={revenirALaFiche}
            onChanger={onChanger}
            typeEtPapier={typeEtPapier}
            habitDuProjet={habitDuProjet}
          />
        </div>
      ) : (
        <>
          <div className="fiche-corps">
            {type !== 'contact' && (
              <TeteDeLaFiche bulle={bulle} mecanique={mecanique} onChanger={onChanger}>
                {!renommer && (
                  <button
                    type="button"
                    className="fiche-crayon"
                    aria-label={RENOMMER_LA_BULLE}
                    title={RENOMMER_LA_BULLE}
                    onClick={() => mecanique.setRenommer(true)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                      <path d="M4 20h4L19 9l-4-4L4 16v4z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </TeteDeLaFiche>
            )}

            {type === 'note' ? (
              <>
                <NoteDeLaFiche bulle={bulle} onChanger={onChanger} />
              </>
            ) : type === 'contact' ? (
              <>
                {/* La carte de visite remplace le titre ; le contact se corrige dans les Réglages. */}
                <ContactDeLaFiche
                  bulle={bulle}
                  mecanique={mecanique}
                  onPoser={() => ouvrirLesReglages('contact')}
                  onChanger={onChanger}
                />
              </>
            ) : type === 'plan' ? (
              <>
                {/* Le schéma, puis ses conditions et ses liens, dépliés ; la note en dernier. */}
                <PlanDeLaFiche key={`plan-${bulle.id}`} bulle={bulle} mere={mere} />
                {conditions}
                <LiensDeLaFiche key={`liens-${bulle.id}`} bulleId={bulle.id} onAutreBulle={onAutreBulle} />
                {note}
              </>
            ) : (
              classique
            )}
          </div>

          <footer className="fiche-pied">
            <div className="fiche-pied-gestes">
              <GestesDuPied
                bulle={bulle}
                annulation={annulation}
                onValider={onValider}
                onAttendre={() => onAttendre()}
                onDefaire={onDefaire}
              >
                <button className="bouton bouton-discret" onClick={onCreuser}>
                  {CREUSER}
                </button>
              </GestesDuPied>
            </div>
            {/*
             * Corriger une erreur ou ranger ailleurs, pas un geste de tous les jours : deux mots
             * soulignés tout en bas, loin de « C'est fait » (décision 3).
             */}
            <div className="fiche-ranger">
              <button
                ref={mecanique.boutonDeplacer}
                type="button"
                className="fiche-ranger-geste"
                data-tuto="fiche-deplacer"
                onClick={mecanique.deplacer}
              >
                {DEPLACER}
              </button>
              <button
                ref={mecanique.boutonSupprimer}
                type="button"
                className="fiche-ranger-geste"
                onClick={mecanique.supprimer}
              >
                {SUPPRIMER_LA_BULLE}
              </button>
            </div>
          </footer>
        </>
      )}
    </CoqueDeLaFiche>
  );
}
